import { batch } from 'react-redux';
import { toDictionary, toList } from '@ff-os/shared-utils/dictionaries';
import { unwrap } from '@ff-os/shared-utils/unwrap';
import { createUserInputsPatch } from '@ff-resource-plugins/prefabs-shared/merge-logic/createUserInputsPatch';
import { actions as inputActions } from '@ff-scene-plugins/gift-redux/slices/inputsSlice';
import { actions as metaActions } from '@ff-scene-plugins/gift-redux/slices/metaSlice';
import { actions as variableActions } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState, GiftPdpStore } from '@ff-scene-plugins/gift-redux/store';
import { matchActiveVariant } from '@ff-scene-plugins/gift-shared/matchActiveVariant';
import { filterPrefabs } from '../_redux-prefabs';

export const preprocess = (store: GiftPdpStore) => {
    const state = store.getState();
    const isContentFirst = state.scene?.options?.contentFirst === true;

    const { inputs, variables, meta } = mergeDefaultPrefab(state);

    batch(() => {

        if (inputs) {
            if (isContentFirst) {
                // In content-first mode, replace all inputs to avoid mixing prefab data
                store.dispatch(inputActions.setAll(toList(inputs)));
            } else {
                store.dispatch(inputActions.upsertMany(toList(inputs)));
            }
        }

        if (variables) {
            if (isContentFirst) {
                // In content-first mode, replace all variables to avoid mixing prefab data
                store.dispatch(variableActions.setAll(toList(variables)));
            } else {
                store.dispatch(variableActions.upsertMany(toList(variables)));
            }
        }

        if (meta) {
            store.dispatch(metaActions.overwrite(meta));
        }
    });
};

const mergeDefaultPrefab = ({ project, resources, tenant, scene }: GiftPdpState) => {

    const { inputs, meta, variables } = project;
    const variants = scene.options.variants;

    const activeVariantId = matchActiveVariant(unwrap(variants), inputs.entities).id;
    const urlPrefabId = typeof window !== 'undefined' ? (window as any).__idt_state?.prefabId : undefined;

    const filteredPrefabs = filterPrefabs(
        resources.prefab!,
        tenant.id,
        tenant.enableAiStudio,
        activeVariantId,
        urlPrefabId);

    const defaultPrefab = (urlPrefabId && filteredPrefabs.find(p => p.id === urlPrefabId))
        || filteredPrefabs[0];

    if (!defaultPrefab) {
        return {
            meta: meta,
            variables: variables.entities,
            inputs: inputs.entities
        };
    }

    const patchedInputs = createUserInputsPatch(inputs, defaultPrefab);

    const pinnedVariables = unwrap(variables).filter(v => v.pinned);

    const finalMeta = {
        public: {
            prefabId: defaultPrefab.id,
            commission: defaultPrefab.commission?.id
        },
        activeVariantId: activeVariantId,
        viewboxWidth: defaultPrefab.viewbox.width,
        viewboxHeight: defaultPrefab.viewbox.height
    };

    const finalVariables = {
        ...toDictionary(pinnedVariables, v => v.id),
        ...defaultPrefab.variables.entities
    };

    let finalInputs: Record<string, any> = {
        ...inputs.entities,
        ...patchedInputs
    };

    // Apply variantInputs from prefab (e.g., { type: { value: "printed" } })
    if (defaultPrefab.variantInputs) {
        for (const [inputId, inputValue] of Object.entries(defaultPrefab.variantInputs)) {
            if (finalInputs[inputId]) {
                finalInputs[inputId] = { ...finalInputs[inputId], value: inputValue };
            } else {
                finalInputs[inputId] = { id: inputId, value: inputValue };
            }
        }
    }

    return {
        meta: finalMeta,
        variables: finalVariables,
        inputs: finalInputs
    };
};